import { ReportCard, ResultsTables, RileyMessage } from '@patchbay/ui'
import * as React from 'react'
import { RileyText } from '../components/riley-text'
import { SearchHitLink } from '../lib/search-hit'
import type { ChatItem } from '../lib/types'
import { chatItemCard, citationsOf } from './chat-item-data'
import { RileyConfirmCard } from './riley/card'
import { RileyCitationChips } from './riley/citations'
import type { RileyCard, RileyFeedbackTarget, RileyRailDataSource } from './riley/contracts'
import { RileyFeedback } from './riley/feedback'
import { createHttpRileySource } from './riley/http'

type FeedbackFn = (feedback: RileyFeedbackTarget) => Promise<void>
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
let httpFeedback: FeedbackFn | null = null
/** Ratings go to `POST /api/v1/riley/feedback`; only a stored message (a UUID id) can be rated. */
const defaultFeedback = (): FeedbackFn => {
  if (!httpFeedback) {
    const source = createHttpRileySource()
    httpFeedback = (f) => source.submitFeedback?.(f) ?? Promise.resolve()
  }
  return httpFeedback
}

// Pure mappers live in chat-item-data (no component imports), so the test setup's fixtures can load them alone.
export { chatItemCard, citationsOf }

/** Search hits as links: a Patchbay route opens in-app, a Desk/Conduit URL in a new tab, no href is plain text. */
function ResultLinks({ links }: { links: NonNullable<ChatItem['links']> }) {
  return (
    <ul className="mt-1 divide-y divide-border overflow-hidden rounded-md border border-border bg-card">
      {links.map((h) => (
        <li key={`${h.kind}:${h.id}`}>
          <SearchHitLink hit={h} className="block px-3 py-1.5 text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none">
            <span className="font-medium">{h.label}</span>
            {h.sublabel ? <span className="ml-2 text-muted-foreground">{h.sublabel}</span> : null}
          </SearchHitLink>
        </li>
      ))}
    </ul>
  )
}

export type CardDecider = Pick<
  RileyRailDataSource,
  'confirmCard' | 'dismissCard' | 'undoBulkCard' | 'tidyServiceHistory' | 'moveToReplyBox'
>

/**
 * Renders a Riley chat stream: messages, result tables and confirm cards, oldest first. A card carrying its real
 * `rileyCard` is confirmed or dismissed through `decider` (the API); without one it is display-only.
 */
export function ChatStream({
  items,
  decider,
  onFeedback,
}: {
  items: ChatItem[]
  decider?: CardDecider
  /** Where thumbs go; defaults to the API. A message without a stored (UUID) id shows none. */
  onFeedback?: FeedbackFn
}) {
  const feedback = onFeedback ?? defaultFeedback()
  const [cards, setCards] = React.useState<Record<string, RileyCard>>({})
  return (
    <>
      {items.map((m) => {
        if (m.card) {
          const real = m.rileyCard
          const card = cards[m.id] ?? real ?? chatItemCard(m as ChatItem & { card: NonNullable<ChatItem['card']> })
          const live = Boolean(real && decider)
          const tidyServiceHistory = decider?.tidyServiceHistory
          return (
            <div key={m.id} className="pl-9">
              <RileyConfirmCard
                card={live ? card : { ...card, can_confirm: false }}
                onConfirm={
                  live && decider
                    ? async (current, edited, acknowledged) => {
                        const next = await decider.confirmCard(current.id, edited, acknowledged)
                        setCards((currentCards) => ({ ...currentCards, [m.id]: next }))
                        return next
                      }
                    : undefined
                }
                onDismiss={
                  live && decider
                    ? async (current, reason) => {
                        const next = await decider.dismissCard(current.id, reason ?? undefined)
                        setCards((currentCards) => ({ ...currentCards, [m.id]: next }))
                        return next
                      }
                    : undefined
                }
                onUseInReplyBox={
                  live && decider?.moveToReplyBox
                    ? async (current) => {
                        const next = (await decider.moveToReplyBox?.(current.id)) ?? current
                        setCards((currentCards) => ({ ...currentCards, [m.id]: next }))
                        return next
                      }
                    : undefined
                }
                onTidyServiceHistory={live && tidyServiceHistory ? (_current, text) => tidyServiceHistory(card.id, text) : undefined}
                onBulkUndo={
                  live && decider?.undoBulkCard
                    ? async (current) => {
                        const next = await decider.undoBulkCard?.(current.id)
                        if (!next) return current
                        setCards((currentCards) => ({ ...currentCards, [m.id]: next }))
                        return next
                      }
                    : undefined
                }
              />
            </div>
          )
        }
        return (
          <div key={m.id}>
            <RileyMessage from={m.role} author={m.author} at={m.at} hypothesis={m.hypothesis}>
              {m.text ? m.role === 'riley' ? <RileyText text={m.text} /> : <p>{m.text}</p> : null}
              {m.links?.length ? <ResultLinks links={m.links} /> : null}
              <RileyCitationChips citations={citationsOf(m)} />
              <ResultsTables tables={m.resultTables} />
              {m.reportCards?.map((report) => (
                <ReportCard key={`${report.report_type}:${report.generated_at}`} report={report} />
              ))}
            </RileyMessage>
            {m.role === 'riley' && UUID.test(m.id) ? (
              <div className="ml-9 mt-1">
                <RileyFeedback id={m.id} target="message" onFeedback={feedback} />
              </div>
            ) : null}
          </div>
        )
      })}
    </>
  )
}
